<template>
  <Bar
    :data="finalChartData"
    :options="finalChartOptions"
    :plugins="plugins"
  />
</template>

<script lang="ts">
import { Bar } from 'vue-chartjs'
import DataLabelsPlugin from 'chartjs-plugin-datalabels'
import type { ChartData as FinalChartData, ChartOptions } from 'chart.js'
import { legendSpacing } from '~/assets/chart/plugins'
import exportedColors from '~/assets/style/_exportDefaultChartColors.module.scss'
import { formatMemberValue } from '~/services/analyticsFormat'
import { FormatContext } from '~/types/analyticsFormat'
import type { ChartData, DataSet } from '~/types/analytics'
import {
  DATALABELS_PLUGIN_OPTIONS,
  DEFAULT_CHART_OPTIONS,
  TOOLTIP_PLUGIN_OPTIONS
} from '~/constants/chart'
import type { ResultSetItemValue } from '~/types/query'

const LEGEND_INCREASE_WIDTH = 16
const LegendSpacingPlugin = legendSpacing<'bar'>(LEGEND_INCREASE_WIDTH)

export default {
  name: 'ChartHorizontalPipe',
  components: { Bar },
  props: {
    chartData: {
      type: Object as PropType<ChartData>,
      required: true
    }
  },
  setup() {
    return {
      plugins: [LegendSpacingPlugin, DataLabelsPlugin]
    }
  },
  computed: {
    finalChartOptions(): ChartOptions<'bar'> {
      return {
        ...DEFAULT_CHART_OPTIONS,
        layout: {
          padding: {
            left: 0,
            right: 0,
            top: 32,
            bottom: 8
          }
        },
        indexAxis: 'x',
        scales: { x: { display: false }, y: { display: false } },
        plugins: {
          legend: {
            display: true,
            position: 'right',
            labels: {
              font: {
                size: 13
              },
              pointStyle: 'rectRounded',
              usePointStyle: true
            }
          },
          tooltip: {
            ...TOOLTIP_PLUGIN_OPTIONS,
            enabled: true,
            callbacks: {
              label: context => {
                const label = context.dataset.label
                const format = (context.dataset as DataSet).format
                const value = context.dataset.data[0] as ResultSetItemValue
                return `${label}: ${formatMemberValue(
                  value,
                  format,
                  FormatContext.CHART,
                  false
                )}`
              }
            }
          },
          datalabels: {
            ...DATALABELS_PLUGIN_OPTIONS,
            color: '#15162b',
            anchor: 'end',
            offset: 0,
            align: 'top',
            font: {
              weight: 'bold'
            },
            formatter: (value, context) => {
              return formatMemberValue(
                value,
                (context.dataset as DataSet).format,
                FormatContext.CHART,
                false
              )
            }
          }
        }
      }
    },
    finalChartData(): FinalChartData<'bar'> {
      const colors = Object.values(exportedColors)

      return {
        ...this.chartData,
        labels: [''],
        datasets: this.chartData.datasets.map((dataset, i) => {
          const color = colors[i]

          return {
            ...dataset,
            backgroundColor: color
          }
        })
      }
    }
  }
}
</script>
